import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";

import highWindowLogo from "../assets/HighWindow_logo.png";
import { drawInvoice4404Template } from "../utils/drawInvoice4404Template";
import { HIGH_WINDOW_LOGO } from "../utils/logoData";
import {
  COMPANY_INFO_EVENT,
  DEFAULT_COMPANY_INFO,
  getSavedCompanyInfo,
  splitAddressLines,
} from "../utils/companyInfo";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value?.$oid) return value.$oid;
  return value.toString?.() || "";
};

const formatInputDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const createEmptyItem = () => ({
  id: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  productId: "",
  description: "",
  quantity: 1,
  unitPrice: "",
  taxRate: 0,
  refCode: "",
  searchQuery: "",
});

const formatMoney = (value, currency = "KWD") => {
  const numeric = Number(value) || 0;
  return `${currency} ${numeric.toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })}`;
};

const normalizeStatusValue = (value) => {
  const normalized = (value || "").toString().toLowerCase();
  if (normalized === "sent" || normalized === "issued") return "unpaid";
  return STATUS_OPTIONS.some((opt) => opt.value === normalized)
    ? normalized
    : "draft";
};

const normalizeSavedItems = (items = [], currency = "KWD") =>
  items.map((item, idx) => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.unitPrice) || 0;
    const taxRate = Number(item.taxRate) || 0;
    const lineSubtotal = quantity * unitPrice;
    const lineTax = Math.max(0, lineSubtotal * (taxRate / 100));
    const lineTotal =
      item.lineTotal !== undefined && item.lineTotal !== null
        ? Number(item.lineTotal)
        : Number((lineSubtotal + lineTax).toFixed(3));

    return {
      id: item.id || item._id || idx,
      productId: normalizeId(item.productId),
      description: item.description || item.name || `Line ${idx + 1}`,
      quantity,
      unitPrice: Number(unitPrice.toFixed(3)),
      taxRate,
      refCode: item.refCode || item.sku || item.productSku || "",
      lineTotal,
      currency,
      searchQuery: item.description || item.name || "",
    };
  });

const formatProductOption = (product) => {
  if (!product) return "";
  const parts = [product.name || "Unnamed item"];
  if (product.refCode || product.sku)
    parts.push(`Ref ${product.refCode || product.sku}`);
  if (product.barcode) parts.push(`Barcode ${product.barcode}`);
  return parts.join(" • ");
};

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft (not issued)" },
  { value: "unpaid", label: "Issued · Unpaid" },
  { value: "partial", label: "Partially paid" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "cancelled", label: "Cancelled" },
  { value: "void", label: "Void" },
];

const STATUS_TONES = {
  draft: "info",
  unpaid: "info",
  paid: "success",
  partial: "warn",
  overdue: "danger",
  cancelled: "danger",
  void: "danger",
};

const PREVIEW_PAGE_WIDTH = 816;
const PREVIEW_PAGE_MIN_HEIGHT = 1050;

function InvoiceBuilderPage({
  customers = [],
  products = [],
  onCancel,
  onSave,
  onCreateCustomer,
  saving = false,
  error = "",
  savedInvoice = null,
  loading = false,
  mode = "create",
  canUpdateStatus = false,
  onLogEvent,
  onRecordPayment,
}) {
  const [form, setForm] = useState(() => ({
    invoiceNumber: "",
    issueDate: formatInputDate(new Date()),
    dueDate: "",
    currency: "KWD",
    customerId: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    billingAddress: "",
    notes: "",
    status: "draft",
  }));
  const [items, setItems] = useState([createEmptyItem()]);
  const [addition, setAddition] = useState({
    label: "",
    value: "",
    mode: "amount",
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    method: "",
    reference: "",
    note: "",
    paidAt: formatInputDate(new Date()),
  });
  const [localError, setLocalError] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [latestSaved, setLatestSaved] = useState(savedInvoice);
  const [logoDataUrl, setLogoDataUrl] = useState(HIGH_WINDOW_LOGO);
  const [companyInfo, setCompanyInfo] = useState(() => getSavedCompanyInfo());
  const [barcodeRender, setBarcodeRender] = useState({
    value: "",
    dataUrl: "",
    error: "",
  });
  const [showHistory, setShowHistory] = useState(false);
  const previewWrapRef = useRef(null);
  const previewCardRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [previewViewport, setPreviewViewport] = useState({
    width: PREVIEW_PAGE_WIDTH,
    height: PREVIEW_PAGE_MIN_HEIGHT,
  });
  const companyAddressLines = useMemo(
    () => splitAddressLines(companyInfo.address),
    [companyInfo],
  );
  const brandDetailLines = useMemo(() => {
    const lines = companyAddressLines.filter((line) => line && line.trim());
    if (companyInfo.email) {
      lines.push(companyInfo.email);
    }
    if (companyInfo.phone) {
      lines.push(companyInfo.phone);
    }
    return lines;
  }, [companyAddressLines, companyInfo.email, companyInfo.phone]);

  useEffect(() => {
    const wrap = previewWrapRef.current;
    const card = previewCardRef.current;
    if (!wrap || !card) return undefined;

    let frame = 0;
    const updateScale = () => {
      const wrapWidth = wrap.clientWidth;
      if (!wrapWidth) return;
      const styles = window.getComputedStyle(wrap);
      const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
      const availableWidth = Math.max(
        0,
        wrapWidth - paddingLeft - paddingRight,
      );
      const cardWidth = card.offsetWidth || PREVIEW_PAGE_WIDTH;
      const cardHeight = card.offsetHeight || PREVIEW_PAGE_MIN_HEIGHT;
      if (!availableWidth || !cardWidth) return;
      const nextScale = Math.min(1, availableWidth / cardWidth);
      const nextWidth = Math.max(1, Math.round(cardWidth * nextScale));
      const nextHeight = Math.max(1, Math.round(cardHeight * nextScale));

      setPreviewScale((prev) =>
        Math.abs(prev - nextScale) > 0.005 ? nextScale : prev,
      );
      setPreviewViewport((prev) =>
        prev.width !== nextWidth || prev.height !== nextHeight
          ? { width: nextWidth, height: nextHeight }
          : prev,
      );
    };

    const scheduleUpdate = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateScale);
    };

    updateScale();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleUpdate);
      observer.observe(wrap);
      observer.observe(card);
    } else {
      window.addEventListener("resize", scheduleUpdate);
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  useEffect(() => {
    return () => {
      if (productSearchBlurTimeout.current) {
        clearTimeout(productSearchBlurTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadLogo = async () => {
      try {
        const response = await fetch(highWindowLogo);
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!cancelled && typeof reader.result === "string") {
            setLogoDataUrl(reader.result);
          }
        };
        reader.readAsDataURL(blob);
      } catch (_err) {
        // ignore failures and keep fallback logo data
      }
    };
    if (typeof window !== "undefined" && highWindowLogo) {
      loadLogo();
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!savedInvoice) {
      setAddition({ label: "", value: "", mode: "amount" });
      setLatestSaved(null);
      return;
    }
    const currency = savedInvoice.currency || "KWD";
    const normalizedStatus = normalizeStatusValue(savedInvoice.status);
    setLatestSaved({ ...savedInvoice, status: normalizedStatus });
    setForm((prev) => ({
      ...prev,
      invoiceNumber: savedInvoice.invoiceNumber || prev.invoiceNumber,
      issueDate: formatInputDate(savedInvoice.issueDate) || prev.issueDate,
      dueDate: formatInputDate(savedInvoice.dueDate) || prev.dueDate,
      currency,
      customerId: savedInvoice.customerId || prev.customerId,
      customerName: savedInvoice.customerName || prev.customerName,
      customerEmail: savedInvoice.customerEmail || prev.customerEmail,
      customerPhone: savedInvoice.customerPhone || prev.customerPhone,
      billingAddress: savedInvoice.billingAddress || prev.billingAddress,
      notes: savedInvoice.notes || prev.notes,
      status: normalizedStatus || prev.status || "draft",
    }));
    if (Array.isArray(savedInvoice.items) && savedInvoice.items.length) {
      setItems(normalizeSavedItems(savedInvoice.items, currency));
    }
    const savedAddition =
      savedInvoice.additionalCharge || savedInvoice.additionalCharges?.[0];
    const additionLabel =
      savedAddition?.label || savedInvoice.additionalChargeTitle || "";
    const additionValueRaw =
      savedAddition?.value ??
      savedAddition?.amount ??
      savedAddition?.percent ??
      savedInvoice.additionalChargeValue;
    const additionMode =
      savedAddition?.mode || savedInvoice.additionalChargeMode || "amount";
    setAddition({
      label: additionLabel || "",
      value:
        additionValueRaw !== undefined && additionValueRaw !== null
          ? String(additionValueRaw)
          : "",
      mode: additionMode === "percent" ? "percent" : "amount",
    });
    setPaymentForm({
      amount: "",
      method: "",
      reference: "",
      note: "",
      paidAt: formatInputDate(new Date()),
    });
  }, [savedInvoice]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleCompanyInfoChange = () => {
      setCompanyInfo(getSavedCompanyInfo());
    };
    window.addEventListener(COMPANY_INFO_EVENT, handleCompanyInfoChange);
    window.addEventListener("storage", handleCompanyInfoChange);
    return () => {
      window.removeEventListener(COMPANY_INFO_EVENT, handleCompanyInfoChange);
      window.removeEventListener("storage", handleCompanyInfoChange);
    };
  }, []);

  const generateBarcodeDataUrl = useCallback(async (value) => {
    if (!value || typeof window === "undefined") return "";
    const { default: JsBarcode } = await import("jsbarcode");
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, {
      format: "CODE128",
      displayValue: false,
      margin: 4,
      height: 48,
      width: 2,
    });
    return canvas.toDataURL("image/png");
  }, []);

  useEffect(() => {
    let cancelled = false;
    const value = latestSaved?.barcodeValue;
    if (!value) {
      setBarcodeRender({ value: "", dataUrl: "", error: "" });
      return undefined;
    }
    const render = async () => {
      try {
        const dataUrl = await generateBarcodeDataUrl(value);
        if (!cancelled) {
          setBarcodeRender({ value, dataUrl, error: "" });
        }
      } catch (err) {
        if (!cancelled) {
          setBarcodeRender({
            value,
            dataUrl: "",
            error: err?.message || "Unable to render barcode.",
          });
        }
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [generateBarcodeDataUrl, latestSaved?.barcodeValue]);

  const productLookup = useMemo(() => {
    const map = new Map();
    (products || []).forEach((prod) => {
      const id = normalizeId(prod.id || prod._id);
      if (id) {
        map.set(id, prod);
      }
    });
    return map;
  }, [products]);
  const productSearchList = useMemo(() => {
    return (products || [])
      .map((prod) => {
        const id = normalizeId(prod.id || prod._id);
        if (!id) return null;
        const name = prod.name || "Unnamed item";
        const refCode = prod.refCode || prod.sku || "";
        const barcode = prod.barcode || "";
        const searchText = [name, refCode, barcode]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return {
          id,
          name,
          refCode,
          barcode,
          label: formatProductOption(prod),
          searchText,
        };
      })
      .filter(Boolean);
  }, [products]);
  const [activeProductSearchId, setActiveProductSearchId] = useState(null);
  const productSearchBlurTimeout = useRef(null);
  const cancelProductSearchClose = () => {
    if (productSearchBlurTimeout.current) {
      clearTimeout(productSearchBlurTimeout.current);
      productSearchBlurTimeout.current = null;
    }
  };

  const selectedCustomer = useMemo(
    () => customers.find((cust) => cust.id === form.customerId),
    [customers, form.customerId],
  );

  const computedItems = useMemo(
    () =>
      items.map((item) => {
        const product = productLookup.get(item.productId);
        const quantity = Number(item.quantity) || 0;
        const unitPrice =
          item.unitPrice === "" ||
          item.unitPrice === null ||
          item.unitPrice === undefined
            ? Number(product?.defaultPrice || 0)
            : Number(item.unitPrice) || 0;
        const taxRate = Number(item.taxRate) || 0;
        const lineSubtotal = quantity * unitPrice;
        const lineTax = Math.max(0, lineSubtotal * (taxRate / 100));
        const lineTotal = lineSubtotal + lineTax;

        return {
          ...item,
          description: item.description || product?.name || "",
          refCode: item.refCode || product?.refCode || product?.sku || "",
          quantity,
          unitPrice: Number(unitPrice.toFixed(3)),
          taxRate,
          lineTotal: Number(lineTotal.toFixed(3)),
          currency: form.currency || "KWD",
        };
      }),
    [form.currency, items, productLookup],
  );

  const validItems = useMemo(
    () =>
      computedItems.filter(
        (item) =>
          item.description &&
          item.quantity > 0 &&
          !Number.isNaN(item.unitPrice),
      ),
    [computedItems],
  );

  const baseTotals = useMemo(() => {
    const subtotal = validItems.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0,
    );
    const taxTotal = validItems.reduce(
      (sum, item) =>
        sum +
        item.quantity * item.unitPrice * ((Number(item.taxRate) || 0) / 100),
      0,
    );
    const total = subtotal + taxTotal;
    return {
      subtotal: Number(subtotal.toFixed(3)),
      taxTotal: Number(taxTotal.toFixed(3)),
      baseTotal: Number(total.toFixed(3)),
    };
  }, [validItems]);

  const additionInfo = useMemo(() => {
    const label = addition.label?.trim() || "";
    const numericValue = Number(addition.value);
    if (!label || Number.isNaN(numericValue) || numericValue === 0) {
      return {
        label,
        value: addition.value,
        mode: addition.mode,
        amount: 0,
        active: false,
        numericValue,
      };
    }
    const rawAmount =
      addition.mode === "percent"
        ? baseTotals.baseTotal * (numericValue / 100)
        : numericValue;
    return {
      label,
      value: addition.value,
      mode: addition.mode,
      amount: Number(rawAmount.toFixed(3)),
      active: true,
      numericValue,
    };
  }, [addition, baseTotals.baseTotal]);

  const totals = useMemo(() => {
    const additionAmount = additionInfo.amount || 0;
    const total = baseTotals.baseTotal + additionAmount;
    return {
      subtotal: baseTotals.subtotal,
      taxTotal: baseTotals.taxTotal,
      addition: additionInfo,
      total: Number(total.toFixed(3)),
    };
  }, [additionInfo, baseTotals]);

  const previewItems = useMemo(
    () => validItems.map((item, idx) => ({ ...item, id: item.id || idx })),
    [validItems],
  );

  const currentStatusOption = useMemo(
    () =>
      STATUS_OPTIONS.find(
        (opt) => opt.value === normalizeStatusValue(form.status),
      ) || STATUS_OPTIONS[0],
    [form.status],
  );
  const currentStatusTone = STATUS_TONES[currentStatusOption.value] || "info";
  const currentRevision = latestSaved?.revision || 1;

  const previewInvoice = useMemo(() => {
    const currency = form.currency || "KWD";
    const balanceFromSaved =
      latestSaved && typeof latestSaved.balance === "number"
        ? Number(latestSaved.balance)
        : null;
    const totalFromSaved =
      latestSaved && typeof latestSaved.total === "number"
        ? Number(latestSaved.total)
        : null;
    const paidTotalFromSaved =
      latestSaved && typeof latestSaved.paidTotal === "number"
        ? Number(latestSaved.paidTotal)
        : null;
    const totalsForPreview = {
      ...totals,
      total: totalFromSaved ?? totals.total,
      balance: balanceFromSaved ?? totals.total,
      paidTotal: paidTotalFromSaved ?? 0,
    };

    const customerName =
      form.customerName || selectedCustomer?.name || "Bill to customer";
    const billingAddress =
      form.billingAddress ||
      selectedCustomer?.address ||
      selectedCustomer?.email ||
      selectedCustomer?.phone ||
      "";

    return {
      invoiceNumber:
        form.invoiceNumber || latestSaved?.invoiceNumber || "Draft",
      issueDate: form.issueDate || latestSaved?.issueDate,
      dueDate: form.dueDate || latestSaved?.dueDate,
      customerName,
      billingAddress,
      customerEmail: form.customerEmail || selectedCustomer?.email || "",
      customerPhone: form.customerPhone || selectedCustomer?.phone || "",
      notes: form.notes || "",
      items: previewItems,
      currency,
      barcodeValue: latestSaved?.barcodeValue || "",
      ...totalsForPreview,
      statusValue: normalizeStatusValue(form.status),
      statusLabel: currentStatusOption.label,
    };
  }, [
    currentStatusOption.label,
    form,
    latestSaved,
    previewItems,
    selectedCustomer,
    totals,
  ]);

  const remainingBalance = useMemo(() => {
    const balanceValue = Number(latestSaved?.balance);
    if (!Number.isNaN(balanceValue)) return balanceValue;
    return totals.total;
  }, [latestSaved?.balance, totals.total]);

  const historyEntries = useMemo(() => {
    const history = Array.isArray(latestSaved?.history)
      ? latestSaved.history
      : [];
    return history
      .slice()
      .sort(
        (a, b) =>
          new Date(b.at || b.updatedAt || b.createdAt || 0).getTime() -
          new Date(a.at || a.updatedAt || a.createdAt || 0).getTime(),
      );
  }, [latestSaved]);

  const billToLines = useMemo(() => {
    const billingAddressLines =
      typeof previewInvoice.billingAddress === "string"
        ? previewInvoice.billingAddress
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
        : Array.isArray(previewInvoice.billingAddress)
          ? previewInvoice.billingAddress
              .map((line) => String(line).trim())
              .filter(Boolean)
          : [];
    return [
      previewInvoice.customerName || "",
      ...billingAddressLines,
      previewInvoice.customerEmail || "",
      previewInvoice.customerPhone || "",
    ].filter((line) => typeof line === "string" && line.trim().length);
  }, [
    previewInvoice.billingAddress,
    previewInvoice.customerEmail,
    previewInvoice.customerName,
    previewInvoice.customerPhone,
  ]);

  const previewNoteLines = useMemo(
    () =>
      typeof previewInvoice.notes === "string"
        ? previewInvoice.notes
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
        : [],
    [previewInvoice.notes],
  );

  const setFormField = (key) => (event) => {
    const value = event?.target?.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCustomerChange = (event) => {
    const customerId = event?.target?.value || "";
    const customer = customers.find((cust) => cust.id === customerId);
    setForm((prev) => ({
      ...prev,
      customerId,
      customerName: customer?.name || prev.customerName,
      customerEmail: customer?.email || prev.customerEmail,
      customerPhone: customer?.phone || prev.customerPhone,
      billingAddress: customer?.address || prev.billingAddress,
    }));
  };

  const handleItemChange = (id, patch) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const handleProductSelect = (id, productId) => {
    cancelProductSearchClose();
    const product = productLookup.get(productId);
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          productId,
          description: item.description || product?.name || "",
          unitPrice:
            item.unitPrice === "" ||
            item.unitPrice === null ||
            item.unitPrice === undefined
              ? (product?.defaultPrice ?? "")
              : item.unitPrice,
          refCode: item.refCode || product?.refCode || product?.sku || "",
          searchQuery: formatProductOption(product),
        };
      }),
    );
    setActiveProductSearchId(null);
  };

  const handleProductSearchChange = (id, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          searchQuery: value,
        };
      }),
    );
  };

  const handleProductClear = (id) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          productId: "",
          description: "",
          quantity: 1,
          unitPrice: "",
          taxRate: 0,
          refCode: "",
          searchQuery: "",
        };
      }),
    );
    setActiveProductSearchId((current) => (current === id ? null : current));
  };
  const handleAdditionField = (key) => (event) => {
    const value = event?.target?.value ?? "";
    setAddition((prev) => ({
      ...prev,
      [key]:
        key === "mode" ? (value === "percent" ? "percent" : "amount") : value,
    }));
  };
  const handleProductSearchBlur = (id) => {
    cancelProductSearchClose();
    productSearchBlurTimeout.current = setTimeout(() => {
      setActiveProductSearchId((current) => (current === id ? null : current));
    }, 120);
  };
  const handleProductSearchFocus = (id) => {
    cancelProductSearchClose();
    setActiveProductSearchId(id);
  };

  const handleAddItem = () => setItems((prev) => [...prev, createEmptyItem()]);

  const handleRemoveItem = (id) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((item) => item.id !== id));
    setActiveProductSearchId((current) => (current === id ? null : current));
  };

  const saveInvoice = async ({ intent = "auto" } = {}) => {
    if (!validItems.length) {
      setLocalError("Add at least one line item with quantity and price.");
      return null;
    }
    if (!form.customerId && !form.customerName) {
      setLocalError("Select a customer to continue.");
      return null;
    }
    setLocalError("");

    const savedStatus = normalizeStatusValue(latestSaved?.status);
    const formStatus = normalizeStatusValue(
      form.status || savedStatus || "draft",
    );
    const targetStatus =
      intent === "draft"
        ? "draft"
        : intent === "issue"
          ? formStatus === "draft"
            ? "unpaid"
            : formStatus
          : formStatus;
    const normalizedTargetStatus = normalizeStatusValue(targetStatus);
    const shouldBumpRevision =
      intent !== "draft" && savedStatus && savedStatus !== "draft";

    if (["partial", "paid"].includes(normalizedTargetStatus) && latestSaved) {
      const invoiceId = normalizeId(latestSaved?._id || latestSaved?.id);
      if (!invoiceId) {
        setLocalError("Save this invoice before recording payments.");
        return null;
      }
      const amountValueRaw =
        normalizedTargetStatus === "paid"
          ? remainingBalance || paymentAmountNumber
          : paymentAmountNumber;
      const amountValue = Number(amountValueRaw);
      const hasNewPayment = !Number.isNaN(amountValue) && amountValue > 0;
      if (!hasNewPayment && existingPayments.length === 0) {
        setLocalError("Enter a payment amount greater than 0.");
        return null;
      }

      if (hasNewPayment) {
        try {
          const recorded = await onRecordPayment?.(invoiceId, {
            amount: amountValue,
            method: paymentForm.method || undefined,
            reference: paymentForm.reference || undefined,
            note: paymentForm.note || undefined,
            paidAt: paymentForm.paidAt || undefined,
          });
          if (!recorded) return null;

          const normalizedInvoice = {
            ...recorded,
            status: normalizeStatusValue(recorded.status),
          };
          setLatestSaved(normalizedInvoice);
          setForm((prev) => ({
            ...prev,
            invoiceNumber:
              normalizedInvoice.invoiceNumber || prev.invoiceNumber,
            issueDate:
              formatInputDate(normalizedInvoice.issueDate) || prev.issueDate,
            dueDate: formatInputDate(normalizedInvoice.dueDate) || prev.dueDate,
            currency: normalizedInvoice.currency || prev.currency,
            status:
              normalizeStatusValue(normalizedInvoice.status) || prev.status,
          }));
          setPaymentForm({
            amount: "",
            method: "",
            reference: "",
            note: "",
            paidAt: formatInputDate(new Date()),
          });
          return normalizedInvoice;
        } catch (err) {
          setLocalError(err?.message || "Unable to record payment.");
          return null;
        }
      }
      // If no new payment but existing payments present, fall through to regular save to update status/PDF.
    }

    let customerId = form.customerId || "";
    let customerDetails = selectedCustomer;
    const typedName = form.customerName?.trim();
    const typedEmail = form.customerEmail?.trim();
    const typedPhone = form.customerPhone?.trim();
    const typedAddress = form.billingAddress?.trim();
    const hasTypedCustomer = Boolean(
      typedName || typedEmail || typedPhone || typedAddress,
    );

    if (!customerId && hasTypedCustomer) {
      const existingCustomer = customers.find((cust) => {
        if (!cust) return false;
        const nameMatch =
          typedName &&
          cust.name?.trim?.().toLowerCase() === typedName.toLowerCase();
        const emailMatch =
          typedEmail &&
          typeof cust.email === "string" &&
          cust.email.trim().toLowerCase() === typedEmail.toLowerCase();
        const phoneMatch =
          typedPhone &&
          typeof cust.phone === "string" &&
          cust.phone.trim() === typedPhone;
        return Boolean(nameMatch || emailMatch || phoneMatch);
      });

      if (existingCustomer) {
        customerId = existingCustomer.id;
        customerDetails = existingCustomer;
        setForm((prev) => ({
          ...prev,
          customerId: existingCustomer.id,
          customerName: prev.customerName || existingCustomer.name || "",
          customerEmail: prev.customerEmail || existingCustomer.email || "",
          customerPhone: prev.customerPhone || existingCustomer.phone || "",
          billingAddress: prev.billingAddress || existingCustomer.address || "",
        }));
      } else if (typedName && typeof onCreateCustomer === "function") {
        try {
          const createdCustomer = await onCreateCustomer({
            name: typedName,
            email: typedEmail,
            phone: typedPhone,
            address: typedAddress,
          });
          const createdId = normalizeId(
            createdCustomer?.id || createdCustomer?._id,
          );
          customerId = createdId || "";
          customerDetails = {
            ...(createdCustomer || {}),
            name: createdCustomer?.name || typedName,
            email:
              createdCustomer?.email ||
              createdCustomer?.contact?.email ||
              typedEmail,
            phone:
              createdCustomer?.phone ||
              createdCustomer?.contact?.phone ||
              typedPhone,
            address: createdCustomer?.address || typedAddress,
          };
          setForm((prev) => ({
            ...prev,
            customerId: createdId || prev.customerId,
            customerName: customerDetails.name || prev.customerName,
            customerEmail: customerDetails.email || prev.customerEmail,
            customerPhone: customerDetails.phone || prev.customerPhone,
            billingAddress: customerDetails.address || prev.billingAddress,
          }));
        } catch (err) {
          setLocalError(err?.message || "Unable to save customer details.");
          return null;
        }
      }
    }

    const payload = {
      invoiceNumber: form.invoiceNumber?.trim() || undefined,
      issueDate: form.issueDate || undefined,
      dueDate: form.dueDate || undefined,
      currency: form.currency || "KWD",
      status: normalizedTargetStatus,
      revisionBump: shouldBumpRevision,
      customerId: customerId || undefined,
      customerName: form.customerName || customerDetails?.name,
      customerEmail:
        form.customerEmail ||
        customerDetails?.email ||
        customerDetails?.contact?.email,
      customerPhone:
        form.customerPhone ||
        customerDetails?.phone ||
        customerDetails?.contact?.phone,
      billingAddress: form.billingAddress || customerDetails?.address,
      notes: form.notes?.trim() || undefined,
      items: validItems.map((item) => ({
        productId: item.productId || undefined,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
        refCode: item.refCode,
      })),
    };
    if (additionInfo.active) {
      payload.additionalCharge = {
        label: additionInfo.label,
        mode: additionInfo.mode,
        value: additionInfo.numericValue,
        amount: additionInfo.amount,
      };
    } else {
      payload.additionalCharge = undefined;
    }

    try {
      const created = await onSave?.(payload);
      if (!created) return null;

      const normalizedStatus = normalizeStatusValue(
        created.status || targetStatus,
      );
      const normalizedInvoice = { ...created, status: normalizedStatus };
      setLatestSaved(normalizedInvoice);
      setForm((prev) => ({
        ...prev,
        invoiceNumber: normalizedInvoice.invoiceNumber || prev.invoiceNumber,
        issueDate:
          formatInputDate(normalizedInvoice.issueDate) || prev.issueDate,
        dueDate: formatInputDate(normalizedInvoice.dueDate) || prev.dueDate,
        currency: normalizedInvoice.currency || prev.currency,
        status: normalizedStatus || prev.status,
      }));
      return normalizedInvoice;
    } catch (err) {
      setLocalError(err?.message || "Unable to save invoice.");
      return null;
    }
  };

  const handleIssueInvoice = async () => {
    setIssuing(true);
    setLocalError("");
    try {
      await saveInvoice({ intent: "issue" });
    } finally {
      setIssuing(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (normalizeStatusValue(form.status) === "draft") {
      setLocalError("Issue this invoice before generating a PDF.");
      return;
    }
    setPdfGenerating(true);
    setLocalError("");
    try {
      const saved = await saveInvoice({ intent: "issue" });
      if (!saved) return;

      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const readableIssueDate =
        previewInvoice.issueDate || formatInputDate(new Date());
      const readableDueDate = previewInvoice.dueDate || "—";
      const pdfBillToLines = billToLines.length
        ? billToLines
        : [previewInvoice.customerName || ""];
      const pdfItems = (previewInvoice.items || []).map((item) => ({
        name: item.description || "",
        qty:
          item.quantity != null && item.quantity !== ""
            ? String(item.quantity)
            : "",
        rate:
          item.unitPrice != null
            ? formatMoney(item.unitPrice, previewInvoice.currency)
            : "",
        amount:
          item.lineTotal != null
            ? formatMoney(item.lineTotal, previewInvoice.currency)
            : "",
      }));
      const additionLabel = previewInvoice.addition?.active
        ? previewInvoice.addition.label || "Additional"
        : "";
      const additionAmount = previewInvoice.addition?.active
        ? formatMoney(previewInvoice.addition.amount, previewInvoice.currency)
        : "";
      const invoiceNumberForPdf =
        saved.invoiceNumber || previewInvoice.invoiceNumber || "invoice";
      const statusValueForPdf = normalizeStatusValue(
        saved.status || previewInvoice.statusValue,
      );
      const statusLabelForPdf =
        STATUS_OPTIONS.find((opt) => opt.value === statusValueForPdf)?.label ||
        statusValueForPdf ||
        "";
      const barcodeValue =
        saved.barcodeValue || latestSaved?.barcodeValue || "";
      let barcodeDataUrl =
        barcodeValue && barcodeRender.value === barcodeValue
          ? barcodeRender.dataUrl || ""
          : "";
      if (barcodeValue && !barcodeDataUrl) {
        try {
          barcodeDataUrl = await generateBarcodeDataUrl(barcodeValue);
        } catch (_err) {
          // Continue PDF generation even if barcode render fails
        }
      }

      drawInvoice4404Template(doc, {
        invoiceNumber: invoiceNumberForPdf || "Draft",
        companyName: companyInfo.name || DEFAULT_COMPANY_INFO.name,
        companyAddrLine1: companyAddressLines[0] || "",
        companyAddrLine2: companyAddressLines[1] || "",
        companyAddrLine3: companyAddressLines[2] || "",
        companyEmail: companyInfo.email || DEFAULT_COMPANY_INFO.email,
        companyPhone: companyInfo.phone || DEFAULT_COMPANY_INFO.phone,
        date: readableIssueDate,
        dueDate: readableDueDate,
        status: statusLabelForPdf,
        currency: previewInvoice.currency,
        balanceDue: formatMoney(
          previewInvoice.balance,
          previewInvoice.currency,
        ),
        billTo: pdfBillToLines,
        items: pdfItems,
        subtotal: formatMoney(previewInvoice.subtotal, previewInvoice.currency),
        additionLabel,
        additionAmount,
        total: formatMoney(previewInvoice.total, previewInvoice.currency),
        notes: previewNoteLines,
        logoDataUrl,
        barcodeValue,
        barcodeDataUrl,
      });
      doc.save(`${invoiceNumberForPdf}.pdf`);

      const invoiceId = normalizeId(saved._id || saved.id);
      if (invoiceId && typeof onLogEvent === "function") {
        try {
          await onLogEvent(invoiceId, {
            action: "pdf-generated",
            note: "Generated from invoice builder",
          });
        } catch (_err) {
          // ignore log failures
        }
      }
    } catch (err) {
      setLocalError(err?.message || "Unable to generate PDF right now.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    if (!payment || !latestSaved) {
      setLocalError(
        "Save this invoice and payment before downloading a receipt.",
      );
      return;
    }
    setPdfGenerating(true);
    setLocalError("");
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const pdfBillToLines = billToLines.length
        ? billToLines
        : [previewInvoice.customerName || ""];
      const pdfItems = (previewInvoice.items || []).map((item) => ({
        name: item.description || "",
        qty:
          item.quantity != null && item.quantity !== ""
            ? String(item.quantity)
            : "",
        rate:
          item.unitPrice != null
            ? formatMoney(item.unitPrice, previewInvoice.currency)
            : "",
        amount:
          item.lineTotal != null
            ? formatMoney(item.lineTotal, previewInvoice.currency)
            : "",
      }));
      const additionLabel = previewInvoice.addition?.active
        ? previewInvoice.addition.label || "Additional"
        : "";
      const additionAmount = previewInvoice.addition?.active
        ? formatMoney(previewInvoice.addition.amount, previewInvoice.currency)
        : "";
      const sortedPayments = existingPayments
        .slice()
        .sort(
          (a, b) =>
            new Date(a?.paidAt || a?.createdAt || 0).getTime() -
            new Date(b?.paidAt || b?.createdAt || 0).getTime(),
        );
      const paymentIndex = sortedPayments.findIndex(
        (p) => normalizeId(p?._id) === normalizeId(payment?._id),
      );
      const paidBefore =
        paymentIndex > 0
          ? sortedPayments
              .slice(0, paymentIndex)
              .reduce((sum, p) => sum + (Number(p?.amount) || 0), 0)
          : 0;
      const paidToDate = Number(
        (paidBefore + (Number(payment.amount) || 0)).toFixed(3),
      );
      const invoiceTotal = Number(previewInvoice.total) || 0;
      const balanceAfter = Math.max(
        0,
        Number((invoiceTotal - paidToDate).toFixed(3)),
      );
      const receiptNumber =
        payment.receiptNumber ||
        `${previewInvoice.invoiceNumber || "INV"}-R${paymentIndex >= 0 ? paymentIndex + 1 : sortedPayments.length || 1}`;
      const paidDate =
        formatInputDate(payment.paidAt || payment.createdAt) ||
        formatInputDate(new Date());
      const paymentsToDate =
        paymentIndex >= 0
          ? sortedPayments.slice(0, paymentIndex + 1)
          : sortedPayments;
      const paymentLines = paymentsToDate.map((p, idx) => {
        const lineDate = formatInputDate(p.paidAt || p.createdAt) || "Date n/a";
        const methodRef = [
          p.method && `via ${p.method}`,
          p.reference && `Ref ${p.reference}`,
        ]
          .filter(Boolean)
          .join(" ");
        const notePart = p.note ? ` (${p.note})` : "";
        return `#${idx + 1}: ${formatMoney(p.amount, previewInvoice.currency)} on ${lineDate}${
          methodRef ? ` ${methodRef}` : ""
        }${notePart}`;
      });
      const receiptNotes = [
        `Payment ${paymentIndex + 1} of ${sortedPayments.length}`,
        ...paymentLines,
        `Paid to date: ${formatMoney(paidToDate, previewInvoice.currency)}`,
        `Remaining balance: ${formatMoney(balanceAfter, previewInvoice.currency)}`,
      ];
      const statusLabelForPdf =
        previewInvoice.statusLabel ||
        STATUS_OPTIONS.find(
          (opt) =>
            opt.value ===
            normalizeStatusValue(previewInvoice.statusValue || form.status),
        )?.label ||
        "Receipt";
      const barcodeValue = latestSaved?.barcodeValue || "";
      let barcodeDataUrl =
        barcodeValue && barcodeRender.value === barcodeValue
          ? barcodeRender.dataUrl || ""
          : "";
      if (barcodeValue && !barcodeDataUrl) {
        try {
          barcodeDataUrl = await generateBarcodeDataUrl(barcodeValue);
        } catch (_err) {
          // Barcode rendering failure should not block receipt generation
        }
      }

      drawInvoice4404Template(doc, {
        docTitle: "RECEIPT",
        docNumber: receiptNumber,
        invoiceNumber: previewInvoice.invoiceNumber,
        companyName: companyInfo.name || DEFAULT_COMPANY_INFO.name,
        companyAddrLine1: companyAddressLines[0] || "",
        companyAddrLine2: companyAddressLines[1] || "",
        companyAddrLine3: companyAddressLines[2] || "",
        companyEmail: companyInfo.email || DEFAULT_COMPANY_INFO.email,
        companyPhone: companyInfo.phone || DEFAULT_COMPANY_INFO.phone,
        date: paidDate,
        dueDate: previewInvoice.dueDate || "—",
        status: `${statusLabelForPdf} · Receipt`,
        currency: previewInvoice.currency,
        balanceDue: formatMoney(balanceAfter, previewInvoice.currency),
        billTo: pdfBillToLines,
        items: pdfItems,
        subtotal: formatMoney(previewInvoice.subtotal, previewInvoice.currency),
        additionLabel,
        additionAmount,
        total: formatMoney(previewInvoice.total, previewInvoice.currency),
        notes: receiptNotes,
        logoDataUrl,
        barcodeValue,
        barcodeDataUrl,
      });
      doc.save(`${receiptNumber || "receipt"}.pdf`);
      const invoiceId = normalizeId(latestSaved?._id || latestSaved?.id);
      if (invoiceId && typeof onLogEvent === "function") {
        try {
          await onLogEvent(invoiceId, {
            action: "receipt-generated",
            note: `Receipt ${receiptNumber}`,
          });
        } catch (_err) {
          // ignore log failures
        }
      }
    } catch (err) {
      setLocalError(err?.message || "Unable to generate receipt right now.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownloadFinalReceipt = async () => {
    if (!latestSaved || !existingPayments.length) {
      setLocalError("No payments recorded yet.");
      return;
    }
    setPdfGenerating(true);
    setLocalError("");
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const pdfBillToLines = billToLines.length
        ? billToLines
        : [previewInvoice.customerName || ""];
      const pdfItems = (previewInvoice.items || []).map((item) => ({
        name: item.description || "",
        qty:
          item.quantity != null && item.quantity !== ""
            ? String(item.quantity)
            : "",
        rate:
          item.unitPrice != null
            ? formatMoney(item.unitPrice, previewInvoice.currency)
            : "",
        amount:
          item.lineTotal != null
            ? formatMoney(item.lineTotal, previewInvoice.currency)
            : "",
      }));
      const additionLabel = previewInvoice.addition?.active
        ? previewInvoice.addition.label || "Additional"
        : "";
      const additionAmount = previewInvoice.addition?.active
        ? formatMoney(previewInvoice.addition.amount, previewInvoice.currency)
        : "";
      const sortedPayments = existingPayments
        .slice()
        .sort(
          (a, b) =>
            new Date(a?.paidAt || a?.createdAt || 0).getTime() -
            new Date(b?.paidAt || b?.createdAt || 0).getTime(),
        );
      const totalPaid = sortedPayments.reduce(
        (sum, p) => sum + (Number(p?.amount) || 0),
        0,
      );
      const balanceAfter = Math.max(
        0,
        Number((Number(previewInvoice.total) - totalPaid).toFixed(3)),
      );
      const receiptNumber = `${previewInvoice.invoiceNumber || "INV"}-RECEIPT`;
      const paymentLines = sortedPayments.map((p, idx) => {
        const lineDate = formatInputDate(p.paidAt || p.createdAt) || "Date n/a";
        const methodRef = [
          p.method && `via ${p.method}`,
          p.reference && `Ref ${p.reference}`,
        ]
          .filter(Boolean)
          .join(" ");
        const notePart = p.note ? ` (${p.note})` : "";
        return `#${idx + 1}: ${formatMoney(p.amount, previewInvoice.currency)} on ${lineDate}${
          methodRef ? ` ${methodRef}` : ""
        }${notePart}`;
      });
      const receiptNotes = [
        "All payments for this invoice:",
        ...paymentLines,
        `Total paid: ${formatMoney(totalPaid, previewInvoice.currency)}`,
        `Remaining balance: ${formatMoney(balanceAfter, previewInvoice.currency)}`,
      ];
      const barcodeValue = latestSaved?.barcodeValue || "";
      let barcodeDataUrl =
        barcodeValue && barcodeRender.value === barcodeValue
          ? barcodeRender.dataUrl || ""
          : "";
      if (barcodeValue && !barcodeDataUrl) {
        try {
          barcodeDataUrl = await generateBarcodeDataUrl(barcodeValue);
        } catch (_err) {
          // Keep receipt generation moving even if barcode render fails
        }
      }

      drawInvoice4404Template(doc, {
        docTitle: "RECEIPT",
        docNumber: receiptNumber,
        invoiceNumber: previewInvoice.invoiceNumber,
        companyName: companyInfo.name || DEFAULT_COMPANY_INFO.name,
        companyAddrLine1: companyAddressLines[0] || "",
        companyAddrLine2: companyAddressLines[1] || "",
        companyAddrLine3: companyAddressLines[2] || "",
        companyEmail: companyInfo.email || DEFAULT_COMPANY_INFO.email,
        companyPhone: companyInfo.phone || DEFAULT_COMPANY_INFO.phone,
        date: formatInputDate(new Date()),
        dueDate: previewInvoice.dueDate || "—",
        status: "Receipt (all payments)",
        currency: previewInvoice.currency,
        balanceDue: formatMoney(balanceAfter, previewInvoice.currency),
        billTo: pdfBillToLines,
        items: pdfItems,
        subtotal: formatMoney(previewInvoice.subtotal, previewInvoice.currency),
        additionLabel,
        additionAmount,
        total: formatMoney(previewInvoice.total, previewInvoice.currency),
        notes: receiptNotes,
        logoDataUrl,
        barcodeValue,
        barcodeDataUrl,
      });
      doc.save(`${receiptNumber}.pdf`);
      const invoiceId = normalizeId(latestSaved?._id || latestSaved?.id);
      if (invoiceId && typeof onLogEvent === "function") {
        try {
          await onLogEvent(invoiceId, {
            action: "receipt-generated",
            note: `Final receipt ${receiptNumber}`,
          });
        } catch (_err) {
          // ignore log failures
        }
      }
    } catch (err) {
      setLocalError(err?.message || "Unable to generate receipt right now.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const inlineError = localError || error;
  const canIssueInvoice = Boolean(
    validItems.length && (form.customerId || form.customerName),
  );
  const canDownloadPdf = canIssueInvoice;
  const isEditMode = mode === "edit";
  const issuedStatus = normalizeStatusValue(latestSaved?.status || form.status);
  const isIssuedLocked =
    isEditMode && issuedStatus !== "draft" && !canUpdateStatus;
  const normalizedFormStatus = normalizeStatusValue(form.status);
  const isDraftStatus = normalizedFormStatus === "draft";
  const isPaidSettled =
    normalizedFormStatus === "paid" && Number(remainingBalance) <= 0;
  const showTopStatusBar = Boolean(latestSaved);
  const showInlineStatusField = !showTopStatusBar && canUpdateStatus;
  const primarySaveLabel = isDraftStatus ? "Save draft" : "Save";
  const pdfCtaLabel = "Generate PDF";
  const billName =
    billToLines[0] || previewInvoice.customerName || "Bill to customer";
  const billDetailLines = billToLines.slice(1);
  const showPaymentPanel =
    ["partial", "paid"].includes(normalizedFormStatus) && Boolean(latestSaved);
  const paymentAmountNumber = Number(paymentForm.amount);
  const lockedCardStyle = isIssuedLocked
    ? { background: "#f6f7f9", border: "1px dashed #c8ced8", opacity: 0.9 }
    : {};
  const existingPayments = Array.isArray(latestSaved?.payments)
    ? latestSaved.payments
    : [];
  const actionBar = (
    <div
      className="builder-actions"
      style={{
        margin: "0 0 12px 0",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
      }}
    >
      {isEditMode && latestSaved?.barcodeValue ? (
        <div className="builder-barcode">
          <div className="muted extra-small">Invoice barcode</div>
          <div className="barcode-number">{latestSaved.barcodeValue}</div>
          {barcodeRender.dataUrl ? (
            <img
              src={barcodeRender.dataUrl}
              alt={`Barcode ${latestSaved.barcodeValue}`}
              className="invoice-barcode-img"
              style={{ width: 240, height: 78 }}
            />
          ) : (
            <div className="muted extra-small">
              {barcodeRender.error
                ? "Barcode unavailable"
                : "Building barcode…"}
            </div>
          )}
        </div>
      ) : (
        <div />
      )}
      <div className="inline-actions">
        <button
          className="primary-btn"
          type="button"
          onClick={() => saveInvoice({ intent: "auto" })}
          disabled={
            saving ||
            loading ||
            pdfGenerating ||
            issuing ||
            !validItems.length ||
            (showPaymentPanel &&
              existingPayments.length === 0 &&
              (!paymentAmountNumber || Number.isNaN(paymentAmountNumber)))
          }
        >
          {saving ? "Saving…" : primarySaveLabel}
        </button>
        {isDraftStatus ? (
          <button
            className="ghost-btn"
            type="button"
            onClick={handleIssueInvoice}
            disabled={
              issuing ||
              pdfGenerating ||
              loading ||
              saving ||
              !canIssueInvoice ||
              (showPaymentPanel &&
                existingPayments.length === 0 &&
                (!paymentAmountNumber || Number.isNaN(paymentAmountNumber)))
            }
          >
            {issuing ? "Issuing…" : "Issue invoice"}
          </button>
        ) : null}
        {!isDraftStatus ? (
          <button
            className="ghost-btn"
            type="button"
            onClick={handleDownloadPdf}
            disabled={
              pdfGenerating ||
              issuing ||
              loading ||
              saving ||
              !canDownloadPdf ||
              (showPaymentPanel &&
                existingPayments.length === 0 &&
                (!paymentAmountNumber || Number.isNaN(paymentAmountNumber)))
            }
          >
            {pdfGenerating ? "Building PDF…" : pdfCtaLabel}
          </button>
        ) : null}
      </div>
    </div>
  );

  useEffect(() => {
    if (!showPaymentPanel) return;
    const normalizedStatus = normalizeStatusValue(form.status);
    setPaymentForm((prev) => ({
      ...prev,
      amount:
        normalizedStatus === "paid"
          ? String(Number((remainingBalance || prev.amount || 0).toFixed(3)))
          : prev.amount,
      paidAt: prev.paidAt || formatInputDate(new Date()),
    }));
  }, [form.status, remainingBalance, showPaymentPanel]);

  return (
    <section className="invoice-builder">
      {loading ? (
        <div className="inline-alert info">Loading invoice…</div>
      ) : null}
      {inlineError ? (
        <div className="inline-alert error" style={{ marginBottom: "8px" }}>
          {inlineError}
        </div>
      ) : null}
      {actionBar}
      <div className="invoice-builder__grid">
        <div className="invoice-column">
          {showTopStatusBar ? (
            <div className="invoice-card invoice-status-card">
              <div className="invoice-status-card__body">
                {canUpdateStatus ? (
                  <div className="invoice-status-card__control">
                    <p className="muted extra-small">Invoice status</p>
                    <label className="field-label" htmlFor="invoice-status-top">
                      Update status
                    </label>
                    <select
                      id="invoice-status-top"
                      value={form.status}
                      onChange={setFormField("status")}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {isIssuedLocked ? (
                      <p className="invoice-status-card__hint muted extra-small">
                        Issued invoices are locked. Update status or record
                        payments here.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="invoice-status-card__control muted extra-small">
                    Status is managed automatically. Admins can override in
                    settings.
                  </div>
                )}
                <div className="invoice-status-card__summary">
                  <span className="muted small">
                    Revision v{currentRevision}
                  </span>
                  <span className={`pill status ${currentStatusTone}`}>
                    {currentStatusOption.label}
                  </span>
                </div>
              </div>
            </div>
          ) : null}
          <div className="invoice-card" style={lockedCardStyle}>
            <header className="invoice-card__header">
              <div>
                <p className="muted small">Invoice details</p>
                <h3 className="invoice-card__title">
                  {isEditMode ? "Edit invoice" : "Client & timing"}
                  {isIssuedLocked ? " 🔒" : ""}
                </h3>
                {isEditMode && latestSaved?.invoiceNumber ? (
                  <div className="invoice-number-banner">
                    #{latestSaved.invoiceNumber}
                  </div>
                ) : null}
              </div>
              {/* Saved pill moved to top status card */}
            </header>
            {isIssuedLocked ? (
              <div
                className="muted extra-small"
                style={{ padding: "6px 0 2px" }}
              >
                Issued invoice — details locked. Update status to change
                lifecycle.
              </div>
            ) : (
              <div className="invoice-grid two-col">
                <div className="field">
                  <div className="field-top">
                    <label className="field-label" htmlFor="invoice-customer">
                      Customer
                    </label>
                    <span className="muted small">required</span>
                  </div>
                  <select
                    id="invoice-customer"
                    value={form.customerId}
                    onChange={handleCustomerChange}
                  >
                    <option value="">Select a customer</option>
                    {customers.map((cust) => (
                      <option key={cust.id} value={cust.id}>
                        {cust.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label
                    className="field-label"
                    htmlFor="invoice-customer-name"
                  >
                    Bill to name
                  </label>
                  <input
                    id="invoice-customer-name"
                    type="text"
                    placeholder="Name that appears on the invoice"
                    value={form.customerName}
                    onChange={setFormField("customerName")}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="invoice-number">
                    Invoice #
                  </label>
                  <input
                    id="invoice-number"
                    type="text"
                    placeholder="Auto-generated if empty"
                    value={form.invoiceNumber}
                    onChange={setFormField("invoiceNumber")}
                  />
                </div>
                {isEditMode && latestSaved?.barcodeValue ? (
                  <div className="field barcode-field">
                    <label className="field-label">Invoice barcode</label>
                    <div className="barcode-preview">
                      {barcodeRender.dataUrl ? (
                        <img
                          src={barcodeRender.dataUrl}
                          alt={`Barcode ${latestSaved.barcodeValue}`}
                          className="invoice-barcode-img"
                        />
                      ) : (
                        <div className="muted extra-small">
                          {barcodeRender.error
                            ? "Barcode unavailable"
                            : "Building barcode…"}
                        </div>
                      )}
                      <div className="barcode-value muted extra-small">
                        {latestSaved.barcodeValue}
                      </div>
                    </div>
                    <p className="muted extra-small">
                      Scans to the same code shown on the PDF/receipt.
                    </p>
                  </div>
                ) : null}
                <div className="field">
                  <label className="field-label" htmlFor="invoice-issue-date">
                    Issue date
                  </label>
                  <input
                    id="invoice-issue-date"
                    type="date"
                    value={form.issueDate}
                    onChange={setFormField("issueDate")}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="invoice-due-date">
                    Due date
                  </label>
                  <input
                    id="invoice-due-date"
                    type="date"
                    value={form.dueDate}
                    onChange={setFormField("dueDate")}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="invoice-currency">
                    Currency
                  </label>
                  <select
                    id="invoice-currency"
                    value={form.currency}
                    onChange={setFormField("currency")}
                  >
                    <option value="KWD">KWD (Kuwaiti Dinar)</option>
                    <option value="USD">USD (US Dollar)</option>
                    <option value="EUR">EUR (Euro)</option>
                  </select>
                </div>
                {showInlineStatusField ? (
                  <div className="field">
                    <label className="field-label" htmlFor="invoice-status">
                      Status
                    </label>
                    <select
                      id="invoice-status"
                      value={form.status}
                      onChange={setFormField("status")}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="field">
                  <label className="field-label" htmlFor="invoice-address">
                    Billing address / notes
                  </label>
                  <textarea
                    id="invoice-address"
                    placeholder="Address that appears on the invoice"
                    value={form.billingAddress}
                    onChange={setFormField("billingAddress")}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="invoice-email">
                    Contact email
                  </label>
                  <input
                    id="invoice-email"
                    type="email"
                    placeholder="customer@email.com"
                    value={form.customerEmail}
                    onChange={setFormField("customerEmail")}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="invoice-phone">
                    Contact phone
                  </label>
                  <input
                    id="invoice-phone"
                    type="tel"
                    placeholder="+965…"
                    value={form.customerPhone}
                    onChange={setFormField("customerPhone")}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="invoice-card" style={lockedCardStyle}>
            <div className="invoice-card__header">
              <div>
                <p className="muted small">Line items</p>
                <h3 className="invoice-card__title">
                  Products & pricing{isIssuedLocked ? " 🔒" : ""}
                </h3>
              </div>
              {!isIssuedLocked ? (
                <button
                  className="ghost-btn small"
                  type="button"
                  onClick={handleAddItem}
                >
                  + Add line
                </button>
              ) : null}
            </div>

            {isIssuedLocked ? (
              <div
                className="muted extra-small"
                style={{ padding: "6px 0 2px" }}
              >
                Items locked after issuing. Change status or create a revision
                to edit.
              </div>
            ) : (
              <>
                <div className="invoice-items-list">
                  {items.map((item) => {
                    const line =
                      computedItems.find((entry) => entry.id === item.id) ||
                      item;
                    const selectedProduct = productLookup.get(item.productId);
                    const searchValue =
                      typeof item.searchQuery === "string" &&
                      item.searchQuery.length
                        ? item.searchQuery
                        : formatProductOption(selectedProduct);
                    const normalizedQuery = (item.searchQuery || "")
                      .trim()
                      .toLowerCase();
                    const matchingProducts = (
                      normalizedQuery
                        ? productSearchList.filter((entry) =>
                            entry.searchText.includes(normalizedQuery),
                          )
                        : productSearchList
                    ).slice(0, 6);
                    const showDropdown = activeProductSearchId === item.id;
                    return (
                      <div key={item.id} className="invoice-item-row">
                        <div className="invoice-item-row__top">
                          <div className="field">
                            <label className="field-label">Product</label>
                            <div className="product-search">
                              <div className="product-search__control">
                                <input
                                  type="text"
                                  placeholder="Search by name, ref code, or barcode"
                                  value={searchValue || ""}
                                  onChange={(event) =>
                                    handleProductSearchChange(
                                      item.id,
                                      event.target.value,
                                    )
                                  }
                                  onFocus={() =>
                                    handleProductSearchFocus(item.id)
                                  }
                                  onBlur={() =>
                                    handleProductSearchBlur(item.id)
                                  }
                                />
                                {showDropdown ? (
                                  <div className="product-search__dropdown">
                                    {matchingProducts.length ? (
                                      matchingProducts.map((option) => (
                                        <button
                                          key={option.id}
                                          type="button"
                                          className="product-search__option"
                                          onMouseDown={(event) =>
                                            event.preventDefault()
                                          }
                                          onClick={() =>
                                            handleProductSelect(
                                              item.id,
                                              option.id,
                                            )
                                          }
                                        >
                                          <span className="product-search__option-title">
                                            {option.name}
                                          </span>
                                          <span className="product-search__option-meta">
                                            {option.refCode
                                              ? `Ref ${option.refCode}`
                                              : "No ref"}{" "}
                                            {option.barcode
                                              ? `• Barcode ${option.barcode}`
                                              : ""}
                                          </span>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="product-search__empty">
                                        {normalizedQuery
                                          ? `No products match “${item.searchQuery || ""}”.`
                                          : "No products available."}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                              {item.productId ? (
                                <button
                                  className="ghost-btn subtle small product-search__clear"
                                  type="button"
                                  onClick={() => handleProductClear(item.id)}
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="invoice-item-row__bottom">
                          <div className="field">
                            <label className="field-label">Line name</label>
                            <input
                              type="text"
                              placeholder="Description on invoice"
                              value={item.description}
                              onChange={(event) =>
                                handleItemChange(item.id, {
                                  description: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="field">
                            <label className="field-label">Qty</label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={item.quantity}
                              onChange={(event) =>
                                handleItemChange(item.id, {
                                  quantity: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="field">
                            <label className="field-label">Price</label>
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              value={item.unitPrice}
                              onChange={(event) =>
                                handleItemChange(item.id, {
                                  unitPrice: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="field">
                            <label className="field-label">Line total</label>
                            <div className="line-total">
                              {formatMoney(line.lineTotal || 0, form.currency)}
                            </div>
                          </div>
                          <div className="field">
                            <label className="field-label">&nbsp;</label>
                            <button
                              className="ghost-btn subtle"
                              type="button"
                              onClick={() => handleRemoveItem(item.id)}
                              disabled={items.length === 1}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!validItems.length ? (
                  <div
                    className="inline-alert error"
                    style={{ marginTop: "8px" }}
                  >
                    Add at least one product line (with quantity and price)
                    before saving.
                  </div>
                ) : null}
                <div className="muted extra-small">
                  Edit price and quantity freely. Leave price blank to use the
                  product default.
                </div>
              </>
            )}
          </div>

          <div className="invoice-card" style={lockedCardStyle}>
            <div className="invoice-card__header">
              <div>
                <p className="muted small">Calculator</p>
                <h3 className="invoice-card__title">
                  Totals{isIssuedLocked ? " 🔒" : ""}
                </h3>
              </div>
            </div>
            {isIssuedLocked ? (
              <div
                className="muted extra-small"
                style={{ padding: "6px 0 2px" }}
              >
                Totals frozen after issuing. Change status or create a revision
                to adjust amounts.
              </div>
            ) : (
              <>
                <div className="invoice-calculator">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <strong>
                      {formatMoney(totals.subtotal, form.currency)}
                    </strong>
                  </div>
                  <div className="addition-config">
                    <label className="field-label">Optional addition</label>
                    <div className="addition-config__inputs">
                      <input
                        type="text"
                        placeholder="Title (e.g. Service fee)"
                        value={addition.label}
                        onChange={handleAdditionField("label")}
                      />
                      <div className="addition-config__value">
                        <input
                          type="number"
                          placeholder="Amount or %"
                          value={addition.value}
                          onChange={handleAdditionField("value")}
                        />
                        <select
                          value={addition.mode}
                          onChange={handleAdditionField("mode")}
                        >
                          <option value="amount">Amount</option>
                          <option value="percent">% of invoice</option>
                        </select>
                      </div>
                    </div>
                    <p className="muted extra-small">
                      {totals.addition.active
                        ? `Adds ${formatMoney(totals.addition.amount, form.currency)} to this invoice.`
                        : "Leave blank to skip this line on the PDF."}
                    </p>
                  </div>
                  {totals.addition.active ? (
                    <div className="summary-row">
                      <span>{totals.addition.label}</span>
                      <strong>
                        {formatMoney(totals.addition.amount, form.currency)}
                      </strong>
                    </div>
                  ) : null}
                  <div className="summary-row total">
                    <span>Total</span>
                    <strong>{formatMoney(totals.total, form.currency)}</strong>
                  </div>
                </div>
                <div className="field" style={{ marginTop: "12px" }}>
                  <label className="field-label" htmlFor="invoice-notes">
                    Notes on invoice
                  </label>
                  <textarea
                    id="invoice-notes"
                    placeholder="Any procedure or patient details to print on the invoice"
                    value={form.notes}
                    onChange={setFormField("notes")}
                  />
                </div>
              </>
            )}
          </div>

          {showPaymentPanel ? (
            <div
              className="invoice-card"
              style={
                isPaidSettled
                  ? { ...lockedCardStyle, marginTop: "12px" }
                  : { marginTop: "12px" }
              }
            >
              <div className="invoice-card__header">
                <div>
                  <p className="muted small">Payment details</p>
                  <h3 className="invoice-card__title">
                    {isPaidSettled
                      ? "Payment history"
                      : normalizeStatusValue(form.status) === "paid"
                        ? "Mark as paid"
                        : "Record partial payment"}
                  </h3>
                </div>
                <div className="muted small" style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 600 }}>Remaining balance</div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#c33e3a",
                    }}
                  >
                    {formatMoney(remainingBalance, previewInvoice.currency)}
                  </div>
                </div>
              </div>
              {isPaidSettled ? (
                <div
                  className="muted extra-small"
                  style={{ padding: "6px 0 2px" }}
                >
                  Invoice fully paid — payment entry locked. Download receipts
                  below.
                </div>
              ) : (
                <>
                  <div className="invoice-grid two-col">
                    <div className="field">
                      <label
                        className="field-label"
                        htmlFor="payment-amount-wide"
                      >
                        Amount ({previewInvoice.currency})
                      </label>
                      <input
                        id="payment-amount-wide"
                        type="number"
                        min="0"
                        step="0.001"
                        value={paymentForm.amount}
                        onChange={(e) =>
                          setPaymentForm((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="field">
                      <label
                        className="field-label"
                        htmlFor="payment-date-wide"
                      >
                        Payment date
                      </label>
                      <input
                        id="payment-date-wide"
                        type="date"
                        value={paymentForm.paidAt}
                        onChange={(e) =>
                          setPaymentForm((prev) => ({
                            ...prev,
                            paidAt: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="field">
                      <label
                        className="field-label"
                        htmlFor="payment-method-wide"
                      >
                        Method
                      </label>
                      <input
                        id="payment-method-wide"
                        type="text"
                        placeholder="Card, cash, transfer…"
                        value={paymentForm.method}
                        onChange={(e) =>
                          setPaymentForm((prev) => ({
                            ...prev,
                            method: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="payment-ref-wide">
                        Reference
                      </label>
                      <input
                        id="payment-ref-wide"
                        type="text"
                        placeholder="Receipt # or ref"
                        value={paymentForm.reference}
                        onChange={(e) =>
                          setPaymentForm((prev) => ({
                            ...prev,
                            reference: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="field" style={{ marginTop: "8px" }}>
                    <label className="field-label" htmlFor="payment-note-wide">
                      Note
                    </label>
                    <input
                      id="payment-note-wide"
                      type="text"
                      placeholder="Optional note on receipt"
                      value={paymentForm.note}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          note: e.target.value,
                        }))
                      }
                    />
                  </div>
                </>
              )}
              {existingPayments.length ? (
                <div className="payments-list" style={{ marginTop: "8px" }}>
                  <div className="muted small" style={{ marginBottom: "4px" }}>
                    Recorded payments ({existingPayments.length})
                  </div>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "grid",
                      gap: "6px",
                    }}
                  >
                    {existingPayments.map((pmt) => (
                      <li
                        key={
                          pmt._id ||
                          `${pmt.amount}-${pmt.paidAt || pmt.createdAt || Math.random()}`
                        }
                        style={{
                          border: "1px solid #ede7e5",
                          borderRadius: "6px",
                          padding: "6px 8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div>
                            <strong>
                              {formatMoney(pmt.amount, previewInvoice.currency)}
                            </strong>{" "}
                            <span className="muted extra-small">
                              {pmt.method || "—"}
                            </span>
                          </div>
                          <div className="muted extra-small">
                            {pmt.paidAt
                              ? formatInputDate(pmt.paidAt)
                              : "Date n/a"}{" "}
                            {pmt.reference ? `· Ref ${pmt.reference}` : ""}
                            {pmt.note ? ` · ${pmt.note}` : ""}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {pmt.receiptNumber ? (
                            <span
                              className="muted extra-small"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              Receipt {pmt.receiptNumber}
                            </span>
                          ) : (
                            <span
                              className="muted extra-small"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              Recorded
                            </span>
                          )}
                          <button
                            className="ghost-btn small"
                            type="button"
                            onClick={() => handleDownloadReceipt(pmt)}
                          >
                            Download receipt
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {isPaidSettled && existingPayments.length ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "6px",
                      }}
                    >
                      <button
                        className="primary-btn small"
                        type="button"
                        onClick={handleDownloadFinalReceipt}
                      >
                        Download final receipt
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <p className="muted extra-small" style={{ marginTop: "8px" }}>
                Payments adjust the balance and audit trail. PDFs/receipts will
                include these updates.
              </p>
            </div>
          ) : null}
        </div>

        <div className="invoice-preview-wrap" ref={previewWrapRef}>
          <div
            className="invoice-preview-viewport"
            style={{
              width: `${previewViewport.width}px`,
              height: `${previewViewport.height}px`,
            }}
          >
            <div
              className="invoice-preview-card"
              ref={previewCardRef}
              style={{ transform: `scale(${previewScale})` }}
            >
              <div className="invoice-preview__page">
                <header className="invoice-preview__header">
                  <div className="invoice-preview__brand">
                    <div className="preview-logo">
                      <img src={highWindowLogo} alt="High Window logo" />
                    </div>
                    <div>
                      <div className="brand-name">
                        {companyInfo.name || DEFAULT_COMPANY_INFO.name}
                      </div>
                      <div className="brand-details">
                        {brandDetailLines.map((line, idx) => (
                          <div key={`${line}-${idx}`}>{line}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="invoice-preview__meta">
                    <div className="invoice-preview__title">INVOICE</div>
                    <div className="invoice-preview__number">
                      <div># {previewInvoice.invoiceNumber}</div>
                      <div className="muted extra-small">
                        Rev v{currentRevision}
                      </div>
                    </div>
                    <div className="invoice-preview__barcode">
                      {previewInvoice.barcodeValue ? (
                        <>
                          {barcodeRender.dataUrl ? (
                            <img
                              src={barcodeRender.dataUrl}
                              alt={`Barcode ${previewInvoice.barcodeValue}`}
                              className="invoice-barcode-img"
                            />
                          ) : (
                            <div className="muted extra-small">
                              {barcodeRender.error
                                ? "Barcode unavailable"
                                : "Building barcode…"}
                            </div>
                          )}
                          <div className="barcode-value muted extra-small">
                            {previewInvoice.barcodeValue}
                          </div>
                        </>
                      ) : (
                        <div className="muted extra-small">
                          Save to generate barcode
                        </div>
                      )}
                    </div>
                  </div>
                </header>

                <div className="invoice-preview__divider" />

                <div className="invoice-preview__info">
                  <div className="invoice-preview__billto">
                    <div className="bill-label">Bill To:</div>
                    <div className="bill-details">
                      <div className="bill-name">{billName}</div>
                      {billDetailLines.length
                        ? billDetailLines.map((line, idx) => (
                            <div key={`${line}-${idx}`} className="muted">
                              {line}
                            </div>
                          ))
                        : null}
                    </div>
                  </div>
                  <div className="invoice-preview__facts">
                    {[
                      {
                        label: "Date:",
                        value:
                          previewInvoice.issueDate ||
                          formatInputDate(new Date()),
                      },
                      {
                        label: "Due date:",
                        value: previewInvoice.dueDate || "—",
                      },
                      {
                        label: "Status:",
                        value: previewInvoice.statusLabel || "—",
                      },
                      {
                        label: "Balance Due:",
                        value: formatMoney(
                          previewInvoice.balance,
                          previewInvoice.currency,
                        ),
                      },
                    ].map((row) => (
                      <div key={row.label} className="invoice-preview__fact">
                        <span className="fact-label">{row.label}</span>
                        <span className="fact-value">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="invoice-preview__table-card">
                  <table className="invoice-preview__table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewInvoice.items && previewInvoice.items.length ? (
                        previewInvoice.items.map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td>
                              <div className="item-name">
                                {item.description}
                              </div>
                            </td>
                            <td className="align-right">{item.quantity}</td>
                            <td className="align-right">
                              {formatMoney(
                                item.unitPrice,
                                previewInvoice.currency,
                              )}
                            </td>
                            <td className="align-right">
                              {formatMoney(
                                item.lineTotal,
                                previewInvoice.currency,
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="muted">
                            Add products to preview the invoice.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="invoice-preview__totals">
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <strong>
                        {formatMoney(
                          previewInvoice.subtotal,
                          previewInvoice.currency,
                        )}
                      </strong>
                    </div>
                    {previewInvoice.addition?.active ? (
                      <div className="summary-row">
                        <span>{previewInvoice.addition.label}:</span>
                        <strong>
                          {formatMoney(
                            previewInvoice.addition.amount,
                            previewInvoice.currency,
                          )}
                        </strong>
                      </div>
                    ) : null}
                    <div className="summary-row total">
                      <span>Total:</span>
                      <strong>
                        {formatMoney(
                          previewInvoice.total,
                          previewInvoice.currency,
                        )}
                      </strong>
                    </div>
                  </div>
                </div>

                {previewNoteLines.length ? (
                  <div className="invoice-preview__notes-card">
                    <div className="muted small">Notes:</div>
                    <div className="invoice-preview__notes-content">
                      {previewNoteLines.map((line, idx) => (
                        <div key={`${line}-${idx}`}>{line}</div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="invoice-preview__watermark">
                  High Window Medical Services
                </div>
              </div>
            </div>
          </div>
          <div className="muted small">PDF export matches this preview.</div>
        </div>
      </div>
      <div className="invoice-card" style={{ marginTop: "16px" }}>
        <div
          className="invoice-card__header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => setShowHistory((prev) => !prev)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="muted small">Audit trail</span>
            <span className="muted extra-small">v{currentRevision}</span>
          </div>
          <div
            className="muted small"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <span>{showHistory ? "▼" : "▶"}</span>
            <span>
              {showHistory ? "Hide history" : "Show history & revisions"}
            </span>
          </div>
        </div>
        {showHistory ? (
          historyEntries.length ? (
            <ul
              className="history-list"
              style={{ listStyle: "none", padding: "8px 0 0", margin: 0 }}
            >
              {historyEntries.slice(0, 12).map((entry) => {
                const timestamp =
                  entry.at || entry.updatedAt || entry.createdAt;
                const readableDate = timestamp
                  ? new Date(timestamp).toLocaleString()
                  : "—";
                const statusChange =
                  entry.statusFrom &&
                  entry.statusTo &&
                  entry.statusFrom !== entry.statusTo
                    ? `${entry.statusFrom} → ${entry.statusTo}`
                    : "";
                return (
                  <li key={entry._id || `${entry.action}-${readableDate}`}>
                    <div
                      className="history-row"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "12px",
                        padding: "8px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div>
                        <div
                          className="history-row__title"
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <strong>{entry.action || "Update"}</strong>
                          {entry.revisionTo ? (
                            <span className="pill status info">
                              v{entry.revisionTo}
                            </span>
                          ) : null}
                        </div>
                        <div className="muted extra-small">
                          {statusChange || "No status change"} ·{" "}
                          {entry.actor || "system"}
                        </div>
                        {entry.notes ? (
                          <div className="muted extra-small">{entry.notes}</div>
                        ) : null}
                      </div>
                      <div className="muted extra-small align-right">
                        {readableDate}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="muted small" style={{ padding: "8px 0" }}>
              Save this invoice to start the audit trail.
            </p>
          )
        ) : null}
      </div>
    </section>
  );
}

export default InvoiceBuilderPage;
